// import { inIframe } from '@/common/bridge/utilities';
import { useBotUser } from '@/stores/useBotUser';
import { useConfig } from '@/stores/useConfig';
import * as Locale from '@/plugins/i18n';
import { emitFloatButton } from '@/common/eventBus';
import { getParentPostMessageTargetOrigin } from '@/common/utils/getParentPostMessageTargetOrigin';
import { CommsCommand } from './comms.enum';
import { DoesNotExistError, ExecutionError, TimeoutError } from './comms.errors';
import type {
  ParentMessageFormat,
  ParentSync,
  WidgetParentCommand,
  GetPayload,
  GetResponse,
  ParentResultFormat,
  ParentListenCommands,
  ParentReauth,
  ParentStartupConfig,
} from './comms.types';
import { randomId } from '@/library/message/lib';
import { logger } from '@/common/api/logger';
import { devLog } from '@/common/debug';
import { parseErrorMessage } from '@/common/error';
import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';

const __PLEASED__SALT_ID = 'PLEASED_SALT';

/** Parent frame origin confirmed after the first validated postMessage reply. */
let pinnedParentMessageOrigin: string | null = null;

const getExpectedParentOrigin = (): string | null => {
  return pinnedParentMessageOrigin ?? getParentPostMessageTargetOrigin();
};

/**
 * Send a message to the parent command.
 * You can use `CommsCommand.action` to define all the commands
 */
export function post<T extends WidgetParentCommand>(action: T, payload?: GetPayload<T>, executionId?: string): void {
  const message: ParentMessageFormat<T> = {
    __PLEASED__SALT_ID,
    command: action,
    payload: {
      executionId: executionId || generateExecutionId(action),
      payload,
    },
  };
  const url = getParentPostMessageTargetOrigin(); //|| import.meta.env.BASE_URL;
  if (!url) {
    devLog.warn('[MODERN_BRIDGE] Could not resolve parent origin.');
    return;
  }
  window.parent.postMessage(JSON.stringify(message), url);
}

export async function startup() {
  const result = await getResult(CommsCommand.get.startup);

  if (!result) {
    throw new Error('Unable to initialize Pleased Chatbot, no config provided');
  }

  syncParentData({
    __PLEASED__SALT_ID,
    type: 'sync',
    payload: {
      config: result.config,
      visitor: result.visitor,
    },
  });

  // Message event listener
  window.addEventListener('message', (event) => {
    if (event.origin !== getExpectedParentOrigin() || !event.data) {
      devLog.warn('[MODERN_BRIDGE] Ignoring non-matching parent message', { event });
      return;
    }

    const parentCommand: ParentListenCommands = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

    if (!validateSalt(parentCommand)) return;

    if (parentCommand?.type === 'sync') syncParentData(parentCommand);
    if (parentCommand?.type === 'widget:re_auth') reauthAction(parentCommand);
  });

  return result;
}

export async function getResult<T extends WidgetParentCommand>(
  action: T,
  payload?: GetPayload<T>,
  { timeout = 10_000 }: { timeout?: number } = {}
): Promise<GetResponse<T>> {
  // if (import.meta.env.DEV && !inIframe()) {
  //   const { DEV_COMMANDS } = await import('@/dev.mock');
  //   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  //   const DEV_COMMAND = (DEV_COMMANDS as any)[action];
  //   if (DEV_COMMAND) {
  //     const devCmd = DEV_COMMAND;
  //     const result = await devCmd(payload);
  //     return result;
  //   }
  // }

  const executionId = generateExecutionId(action);

  // Listen for the response.
  const result = new Promise<Awaited<GetResponse<T>>>((resolve, reject) => {
    const onResponse = (responsePayload: MessageEvent) => {
      if (responsePayload.origin !== getExpectedParentOrigin()) {
        devLog.warn('[MODERN_BRIDGE] Ignoring non-matching parent message');
        return;
      }

      let response: ParentResultFormat<T>;

      try {
        // Parent (modern loader) always posts JSON strings; ignore any other postMessage traffic
        // (objects, devtools, extensions) so we only resolve when our execution reply arrives.
        if (typeof responsePayload.data !== 'string') {
          return;
        }
        response = JSON.parse(responsePayload.data);
        if (!validatePayload(response, executionId)) return;
      } catch (error) {
        // Malformed or unrelated string payloads — wait for the real reply or timeout.
        logger.error('[comms] Ignoring non-matching parent message', { error: parseErrorMessage(error) });
        return;
      }

      window.removeEventListener('message', onResponse);

      if (!response.error) {
        if (pinnedParentMessageOrigin == null) {
          pinnedParentMessageOrigin = responsePayload.origin;
        }
        if (action === CommsCommand.get.startup && response.result != null) {
          resolve({
            ...(response.result as unknown as ParentStartupConfig),
            clientOrigin: responsePayload.origin,
          } as Awaited<GetResponse<T>>);
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resolve(response.result as any);
        return;
      }

      if (response.error === 'does_not_exists') {
        // create does not exist error
        reject(new DoesNotExistError(action));
        return;
      }

      reject(new ExecutionError(response.error));
    };

    window.addEventListener('message', onResponse);
    post(action, payload, executionId);

    setTimeout(() => {
      reject(new TimeoutError(action));
    }, timeout);
  });
  return result;
}

function syncParentData(sync: ParentSync) {
  const { setConfig } = useConfig();
  if (sync.payload.config) {
    setConfig({ ...sync.payload.config });
    if (sync.payload.config.locale) {
      Locale.setLanguage(sync.payload.config.locale as Locale.SupportedLanguages);
    }
  }
  const { setGuestDetail } = useBotUser();
  if (sync.payload.visitor) {
    setGuestDetail(sync.payload.visitor);
  }
  if (sync.payload.state) {
    sync.payload.state.isOpen && emitFloatButton(sync.payload.state.isOpen === 'closed');
  }
}

function reauthAction(_action: ParentReauth) {
  const auth = useBotUser();
  void auth.checkUserAuthenticated().catch((error: unknown) => {
    captureEventException(error, ErrorTrackingEvent.WidgetAuth);
  });
}

const generateExecutionId = (command: string) => {
  return `execute-${command}-${randomId()}`;
};

const validateSalt = (data: { __PLEASED__SALT_ID?: string }) => {
  return data?.__PLEASED__SALT_ID == __PLEASED__SALT_ID;
};

const validatePayload = (data: { executionId?: string; __PLEASED__SALT_ID?: string }, executionId: string) => {
  return data?.executionId === executionId && validateSalt(data);
};
